import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Coins, ArrowDownRight, ArrowUpRight, RefreshCw, Plus, BarChart3, FileText, Download, Loader2 } from "lucide-react";
import useWalletStore from "../../store/wallet.store";
import { getWalletTransactions } from "../../api/wallet.api";
import StorageMeter from "../../components/wallet/StorageMeter";
import WhatYouCanDo from "../../components/wallet/WhatYouCanDo";

// Human-readable labels for feature codes that appear on debit txns.
const FEATURE_LABELS = {
  ai_tagging: "AI auto-tag",
  ai_suggest_ball: "AI Suggest",
  ai_ingest: "AI ingest",
  match_summary_report: "Match summary",
  match_report_pdf: "Match PDF",
  commentary_regenerate_ball: "Commentary regen",
  chat_haiku_msg: "Chat (Haiku)",
  chat_sonnet_msg: "Chat (Sonnet)",
  voice_ball_charge: "Voice scoring",
};

const REASON_LABELS = {
  TOPUP_PURCHASE:     { label: "Topup",            tone: "credit" },
  FEATURE_REDEMPTION: { label: "AI feature",       tone: "debit" },
  SUBSCRIPTION_GRANT: { label: "Plan Zencredits",  tone: "credit" },
  TRIAL_GRANT:        { label: "Trial Zencredits", tone: "credit" },
  REFUND:             { label: "Refund",           tone: "credit" },
  REVERSAL:           { label: "Auto-refund",      tone: "credit" },
  ADMIN_ADJUST:       { label: "Adjustment",       tone: "neutral" },
  EXPIRY:             { label: "Expired",          tone: "debit" },
};

function fmtTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

export default function Wallet() {
  const balance = useWalletStore((s) => s.balance);
  const transactions = useWalletStore((s) => s.transactions);
  const loading = useWalletStore((s) => s.loading);
  const fetchBalance = useWalletStore((s) => s.fetchBalance);
  const fetchTransactions = useWalletStore((s) => s.fetchTransactions);

  useEffect(() => {
    fetchBalance();
    fetchTransactions(100);
  }, [fetchBalance, fetchTransactions]);

  // Re-fetch when the tab regains focus (covers the "user paid on Cashfree,
  // came back, then switched to /account/wallet" path where balance might
  // be stale by minutes).
  useEffect(() => {
    const onFocus = () => {
      fetchBalance();
      fetchTransactions(100);
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [fetchBalance, fetchTransactions]);

  // If a topup is mid-flight (sessionStorage from Topup.jsx pre-redirect),
  // poll for up to ~150s so the user lands on /account/wallet and sees the
  // credit arrive even without going back to /account/topup. Same poll
  // budget as Topup.jsx so behaviour is consistent across return paths.
  useEffect(() => {
    let pending = null;
    try {
      const raw = sessionStorage.getItem("zcd_pending_topup");
      if (raw) pending = JSON.parse(raw);
    } catch { pending = null; }
    if (!pending || !pending.startBalance && pending.startBalance !== 0) return;
    // Drop stale flags (>10 min old) so a leftover sessionStorage doesn't
    // poll forever after a crashed/cancelled topup.
    if (Date.now() - (pending.ts || 0) > 10 * 60 * 1000) {
      try { sessionStorage.removeItem("zcd_pending_topup"); } catch {}
      return;
    }
    const startBalance = pending.startBalance;
    let tries = 0;
    const MAX_TRIES = 100;       // 150s @ 1.5s
    const poll = setInterval(async () => {
      tries++;
      const newBal = await fetchBalance();
      if (newBal && newBal.balance_credits > startBalance) {
        clearInterval(poll);
        // Clear the pending flag so a navigation back doesn't re-poll.
        try { sessionStorage.removeItem("zcd_pending_topup"); } catch {}
        fetchTransactions(100);   // refresh the table too
      } else if (tries >= MAX_TRIES) {
        clearInterval(poll);
      }
    }, 1500);
    return () => clearInterval(poll);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const credits = balance?.balance_credits ?? 0;
  const locked = !!balance?.is_locked;

  // CSV export of the full transaction history (capped server-side at
  // 10_000 rows). Independent of the page-rendered table — accountants
  // and tax preparers need the full ledger, not just the last 100.
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  async function downloadCsv() {
    setExporting(true);
    setExportError(null);
    try {
      const res = await getWalletTransactions(10000);
      const txns = res?.data?.transactions || [];
      if (!txns.length) {
        setExportError("No transactions to export yet.");
        return;
      }
      const header = [
        "created_at", "reason", "feature_code",
        "delta_credits", "balance_after",
        "external_ref", "notes",
      ];
      const lines = [header.join(",")];
      const safe = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      for (const tx of txns) {
        lines.push([
          tx.created_at,
          tx.reason,
          tx.feature_code || "",
          tx.delta_credits,
          tx.balance_after,
          safe(tx.external_ref || ""),
          safe(tx.notes || ""),
        ].join(","));
      }
      const blob = new Blob([lines.join("\n")], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `wallet-${stamp}-${txns.length}rows.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportError(e?.response?.data?.detail || e.message || "Export failed");
    } finally {
      setExporting(false);
    }
  }

  // Compute this-month spend per feature so users know where credits go.
  // Reads only from already-loaded transactions — no extra API call.
  // FEATURE_REDEMPTION txns have delta_credits negative; flip the sign
  // and group by feature_code. Skip txns from outside this calendar month.
  const usageThisMonth = useMemo(() => {
    if (!transactions?.length) return [];
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    const totals = {};
    let totalSpent = 0;
    for (const tx of transactions) {
      if (tx.reason !== "FEATURE_REDEMPTION") continue;
      if (new Date(tx.created_at).getTime() < monthStart) continue;
      const code = tx.feature_code || "unknown";
      const cost = Math.abs(tx.delta_credits || 0);
      totals[code] = (totals[code] || 0) + cost;
      totalSpent += cost;
    }
    const rows = Object.entries(totals)
      .map(([code, spent]) => ({
        code,
        label: FEATURE_LABELS[code] || code,
        spent,
        pct: totalSpent > 0 ? (spent / totalSpent) * 100 : 0,
      }))
      .sort((a, b) => b.spent - a.spent);
    return { rows, totalSpent };
  }, [transactions]);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-text-primary">Wallet</h1>
          <p className="text-sm text-text-secondary mt-1">
            Zencredits power AI commentary, ball tagging, match-report PDFs, and chat assistant.
          </p>
        </div>
        <button
          onClick={() => { fetchBalance(); fetchTransactions(100); }}
          className="text-text-secondary hover:text-text-primary flex items-center gap-1.5 px-2 py-1 rounded hover:bg-bg-secondary/40 text-sm"
          aria-label="Refresh"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Balance card — stacks on phone so the two CTAs aren't crushed
          into the balance label at 360px. */}
      <div className="bg-bg-primary border border-border-default rounded-xl p-4 sm:p-6 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-4">
          <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
            locked ? "bg-rose-500/15" : credits === 0 ? "bg-amber-500/15" : "bg-emerald-500/15"
          }`}>
            <Coins className={`w-7 h-7 ${
              locked ? "text-rose-400" : credits === 0 ? "text-amber-400" : "text-emerald-400"
            }`} />
          </div>
          <div>
            <p className="text-text-secondary text-xs uppercase tracking-wider">Current balance</p>
            <p className="text-3xl font-bold text-text-primary tabular-nums">{credits} {credits === 1 ? "Zencredit" : "Zencredits"}</p>
            <p className="text-xs text-text-secondary mt-0.5">1 Zencredit = ₹1</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to="/account/invoices"
            className="flex items-center gap-1.5 bg-bg-secondary border border-border-default text-text-primary px-3 py-2 rounded-md hover:border-button-primary text-sm"
          >
            <FileText className="w-4 h-4" />
            Tax invoices
          </Link>
          <Link
            to="/account/storage"
            className="flex items-center gap-1.5 bg-bg-secondary border border-border-default text-text-primary px-4 py-2 rounded-md hover:border-button-primary text-sm"
          >
            Manage storage
          </Link>
          <Link
            to="/account/topup"
            className="flex items-center gap-1.5 bg-button-primary text-white px-4 py-2 rounded-md hover:opacity-90"
          >
            <Plus className="w-4 h-4" />
            Buy Zencredits
          </Link>
        </div>
      </div>

      {locked && (
        <div className="bg-rose-500/10 border border-rose-500/40 rounded-md p-4 mb-6 text-sm text-rose-200">
          <strong>Wallet locked.</strong> {balance?.lock_reason || "Please contact support."}
        </div>
      )}

      {/* Storage meter — hidden if plan is unlimited */}
      <div className="mb-6">
        <StorageMeter />
      </div>

      {/* What you can do with Zencredits — feature pricing strip */}
      <div className="mb-6">
        <WhatYouCanDo />
      </div>

      {/* This-month usage breakdown — surfaces "where are my credits going?"
          Only rendered if there's at least one spending transaction this
          calendar month; first-time users skip the empty chart entirely. */}
      {usageThisMonth?.rows?.length > 0 && (
        <div className="mb-6 bg-bg-primary border border-border-default rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-text-secondary" />
            <h2 className="font-semibold text-text-primary">This month so far</h2>
            <span className="ml-auto text-sm text-text-secondary tabular-nums">
              {usageThisMonth.totalSpent} credits spent
            </span>
          </div>
          <div className="space-y-2">
            {usageThisMonth.rows.map((row) => (
              <div key={row.code} className="flex items-center gap-3 text-sm">
                <div className="w-32 shrink-0 text-text-secondary truncate">
                  {row.label}
                </div>
                <div className="flex-1 bg-bg-secondary rounded h-2 overflow-hidden">
                  <div
                    className="h-full bg-emerald-500/60 rounded"
                    style={{ width: `${row.pct}%` }}
                  />
                </div>
                <div className="w-20 text-right tabular-nums text-text-primary">
                  {row.spent} ZC
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Transactions */}
      <div className="bg-bg-primary border border-border-default rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-border-default flex items-center justify-between gap-3">
          <div className="flex items-baseline gap-3">
            <h2 className="font-semibold text-text-primary">Recent activity</h2>
            <span className="text-xs text-text-secondary">Last 100 transactions</span>
          </div>
          <button
            onClick={downloadCsv}
            disabled={exporting}
            title="Download full ledger as CSV (up to 10,000 rows)"
            className="text-text-secondary hover:text-text-primary flex items-center gap-1.5 px-2 py-1 rounded hover:bg-bg-secondary/40 text-sm disabled:opacity-40"
          >
            {exporting
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <Download className="w-4 h-4" />}
            Export CSV
          </button>
        </div>
        {exportError && (
          <div className="px-6 py-2 text-xs text-rose-200 bg-rose-500/10 border-b border-rose-500/30">
            {exportError}
          </div>
        )}
        {transactions.length === 0 && !loading ? (
          <div className="px-6 py-12 text-center text-sm text-text-secondary">
            No transactions yet — your wallet activity will show up here.
          </div>
        ) : (
          <div className="divide-y divide-border-default">
            {transactions.map((tx) => {
              const meta = REASON_LABELS[tx.reason] || { label: tx.reason, tone: "neutral" };
              const isCredit = tx.delta_credits >= 0;
              return (
                <div key={tx.id} className="px-6 py-3 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                      isCredit ? "bg-emerald-500/15" : "bg-rose-500/15"
                    }`}>
                      {isCredit
                        ? <ArrowDownRight className="w-4 h-4 text-emerald-400" />
                        : <ArrowUpRight   className="w-4 h-4 text-rose-400" />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-text-primary truncate">
                        {meta.label}
                        {tx.feature_code && (
                          <span className="ml-2 text-xs text-text-secondary">— {tx.feature_code}</span>
                        )}
                      </p>
                      <p className="text-xs text-text-secondary truncate">
                        {fmtTime(tx.created_at)}
                        {tx.notes && <span className="ml-2 italic">· {tx.notes}</span>}
                      </p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`text-sm font-semibold tabular-nums ${
                      isCredit ? "text-emerald-300" : "text-rose-300"
                    }`}>
                      {isCredit ? "+" : ""}{tx.delta_credits}
                    </p>
                    <p className="text-[10px] text-text-secondary tabular-nums">
                      bal: {tx.balance_after}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
